import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { OrganizationProfileResolver } from './account-team/data-resolvers/organization-profile.resolver';
import { AuthGuard } from '@auth0/auth0-angular';
import { AdminGuard } from './guards/admin.guard';
import { FeatureFlagGuard } from './guards/feature-flag.guard';
import { UserRoleGuard } from './guards/user-role.guard';
import { OrganizationRoleTypes } from './store/user/user.enum';

const routes: Routes = [
  // the dashboard should be the default home page
  {
    path: '',
    redirectTo: '/dashboard',
    pathMatch: 'full',
  },
  // lazy load the dashboard module
  {
    path: 'dashboard',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./routes/dashboard-section/dashboard-section.module').then(
        (module) => module.DashboardSectionModule,
      ),
  },
  // redirect to dashboard when there is no id
  {
    path: 'project',
    redirectTo: '/dashboard',
    pathMatch: 'full',
  },
  {
    path: 'project',
    canActivate: [AuthGuard],
    loadChildren: () => import('./project/project.module').then((module) => module.ProjectModule),
  },
  // redirect to dashboard when there is no id
  {
    path: 'project-configs',
    redirectTo: '/dashboard',
    pathMatch: 'full',
  },
  // lazy load the project configs module
  {
    path: 'project-configs',
    canActivate: [AuthGuard, UserRoleGuard],
    data: {
      disallowedRoles: [OrganizationRoleTypes.Analytics],
    },
    loadChildren: () =>
      import('./project-config/project-config.module').then((module) => module.ProjectConfigModule),
  },
  {
    path: '',
    loadChildren: () =>
      import('./routes/public-section/public-section.module').then(
        (module) => module.PublicSectionModule,
      ),
  },
  {
    path: 'admin',
    canActivate: [AuthGuard, AdminGuard],
    loadChildren: () => import('./admin/admin.module').then((module) => module.AdminModule),
  },
  // lazy load account and team pages
  {
    path: 'account-team',
    canActivate: [AuthGuard, UserRoleGuard],
    data: {
      disallowedRoles: [OrganizationRoleTypes.Analytics],
    },
    loadChildren: () =>
      import('./account-team/account-team-config.module').then(
        (module) => module.AccountTeamConfigModule,
      ),
    resolve: {
      organizationProfile: OrganizationProfileResolver,
    },
  },
  {
    path: 'create',
    canActivate: [AuthGuard, UserRoleGuard],
    data: {
      disallowedRoles: [OrganizationRoleTypes.Analytics],
    },
    loadChildren: () =>
      import('./designer/designer.module').then((module) => module.DesignerModule),
  },
  // lazy load the api keys module
  {
    path: 'api-keys',
    canActivate: [AuthGuard, UserRoleGuard],
    data: {
      disallowedRoles: [OrganizationRoleTypes.Analytics],
    },
    loadChildren: () =>
      import('./routes/api-keys-section/api-keys-section.module').then(
        (module) => module.ApiKeysSectionModule,
      ),
  },
  {
    path: 'manage-skills',
    canActivate: [AuthGuard, FeatureFlagGuard, UserRoleGuard],
    data: {
      featureFlag: 'manage-skills-form',
      disallowedRoles: [OrganizationRoleTypes.Analytics],
    },
    loadChildren: () =>
      import('./routes/manage-skills-section/manage-skills-section.module').then(
        (module) => module.ManageSkillsSectionModule,
      ),
  },
  {
    path: 'organization-invitation',
    loadChildren: () =>
      import('./organization-invitation/organization-invitation.module').then(
        (module) => module.OrganizationInvitationModule,
      ),
  },
  {
    path: 'manage-credentials',
    canActivate: [AuthGuard, UserRoleGuard],
    data: {
      disallowedRoles: [OrganizationRoleTypes.Analytics, OrganizationRoleTypes.Editor],
    },
    loadChildren: () =>
      import('./manage-credentials/manage-credentials.module').then(
        (module) => module.ManageCredentialsModule,
      ),
  },
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.routes').then((module) => module.AUTH_ROUTES),
  },
  {
    path: 'onboarding',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./onboarding/onboarding.routes').then((module) => module.ONBOARDING_ROUTES),
  },
  // lazy load error module
  {
    path: '**',
    loadChildren: () => import('./error/error.module').then((module) => module.ErrorModule),
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      onSameUrlNavigation: 'reload',
      scrollPositionRestoration: 'top',
    }),
  ],
  providers: [OrganizationProfileResolver],
  exports: [RouterModule],
})
export class AppRoutingModule {}
